import './MyBlockMonth.scss';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import Frame, { FrameContextConsumer } from 'react-frame-component';
import {
  addReserveToPlanned,
  assignSpecialEventStripCalendarHeightLevel,
  checkDataFromBlockMonthApiResponse,
  combineDO6,
  combineRAPsWithMiddleRelease,
  extendReserveToGDO6,
  mapReleaseToParentActivity,
  removeEventsNotInBlockMonth,
  sortBlockMonthActivities,
  SPECIAL_EVENT_TYPES
} from './myBlockMonthHelper';
import AcDialog from '../../components/AcDialog/AcDialog';
import CalendarHeader from '../../components/CalendarHeader/CalendarHeader';
import EventDetailsView from '../../components/EventDetails/EventDetails';
import Loader from '../../components/Loader/Loader';
import MyBlockMonthHeader from '../../components/MyBlockMonthHeader/MyBlockMonthHeader';
import MyBlockMonthNoData from '../../components/MyBlockMonthNoData/MyBlockMonthNoData';
import MyBlockMonthOverview from '../../components/MyBlockMonthOverview/MyBlockMonthOverview';
import StandardCalendar from '../../components/StandardCalendar/StandardCalendar';
import StripCalendar from '../../components/StripCalendar/StripCalendar';
import StripCalendarLegend from '../../components/StripCalendarLegend/StripCalendarLegend';
import { months } from '../../constants/dates';
import { blockType, eventActivityType } from '../../constants/myBlockMonth';
import { formatNumToTime } from '../../helpers/timeHelpers';
import { SearchDate, Activity } from '../../models/MyBlockMonthInterface';
import { useFreezePeriod } from '../../providers/FreezePeriod';
import { useMyBlockMonthProvider } from '../../providers/MyBlockMonthProvider';
import { useProfile } from '../../providers/Profile';
import AcBackToTopButton from '../../components/AcBackToTopButton/AcBackToTopButton';
import MyBlockMonthPrintDialog from '../../components/SyncToCalDialog/MyBlockMonthPrintDialog/MyBlockMonthPrintDialog';

const MyBlockMonth = () => {
  const freezePeriodContext = useFreezePeriod();
  const myBlockMonthContext = useMyBlockMonthProvider();
  const profileContext = useProfile();
  const location = useLocation();
  const stdCalPrintRef = useRef(null) as any;
  const [printing, setPrinting] = useState(false) as any;
  const { t } = useTranslation();

  const [actual, setPlannedToActual] = useState(true);
  const [checked, setChecked] = useState(false);
  const [datesDropDownIndex, setDatesDropDownIndex] = useState(0);
  const [eventList, setEventList] = useState({
    events: [] as Activity[],
    holidayEvents: [] as Activity[]
  });
  const [isNonBidder, setIsNonBidder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [reservePilot, setReservePilot] = useState(false);
  const [searchDate, setSearchDate] = useState<SearchDate>({} as any);
  const [selectedEventIndex, setSelectedEventIndex] = useState(-1);
  const [stripView, setChangeToStripView] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [viewAll, setViewAll] = useState(false);
  const [printDialog, setPrintDialog] = useState(false);
  const printView = useRef('portrait');

  useEffect(() => {
    myBlockMonthContext.getMyBlockMonthDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (!myBlockMonthContext.myBlockMonthDates.length) return;
    if (
      searchDate.month ===
        months.findIndex(
          (month) =>
            month === myBlockMonthContext.myBlockMonthDates[0].BID_PERIOD_MNTH
        ) +
          1 &&
      searchDate.year === myBlockMonthContext.myBlockMonthDates[0].BID_PERIOD_YR
    )
      return;
    if (sessionStorage.getItem('block-month-notification')) {
      const notificationParams = JSON.parse(
        String(sessionStorage.getItem('block-month-notification'))
      );
      setDatesDropDownIndex(
        myBlockMonthContext.myBlockMonthDates.findIndex(
          (d) => d.BID_PERIOD_MNTH === months[notificationParams.month - 1]
        )
      );
      setSearchDate({
        month: notificationParams.month,
        year: notificationParams.year
      });
      sessionStorage.removeItem('block-month-notification');
    } else {
      let month =
        months.findIndex(
          (m) => m === myBlockMonthContext.myBlockMonthDates[0].BID_PERIOD_MNTH
        ) + 1;
      let year = myBlockMonthContext.myBlockMonthDates[0].BID_PERIOD_YR;
      if (
        freezePeriodContext.freezePeriodData.flyingSchedule[1] &&
        freezePeriodContext.freezePeriodData.flyingSchedule[1].month &&
        freezePeriodContext.freezePeriodData.flyingSchedule[1].year
      ) {
        month = freezePeriodContext.freezePeriodData.flyingSchedule[1].month;
        year = freezePeriodContext.freezePeriodData.flyingSchedule[1].year;
        setDatesDropDownIndex(
          myBlockMonthContext.myBlockMonthDates.findIndex(
            (d) => d.BID_PERIOD_MNTH === months[month - 1]
          )
        );
      } else {
        setDatesDropDownIndex(0);
      }
      setSearchDate({
        month: month,
        year: year
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBlockMonthContext.myBlockMonthDates]);

  useEffect(() => {
    if (!Object.keys(searchDate).length) return;
    myBlockMonthContext.getMyBlockMonthData(searchDate, actual);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDate, actual]);

  useEffect(() => {
    if (
      !Object.keys(searchDate).length ||
      !Object.keys(myBlockMonthContext.myBlockMonthData).length ||
      (!myBlockMonthContext.myBlockMonthData.activities.length &&
        myBlockMonthContext.myBlockMonthData.blockList?.BLOCK_TYPE_MNT &&
        myBlockMonthContext.myBlockMonthData.blockList?.BLOCK_TYPE_MNT !==
          blockType.NONBIDDER)
    ) {
      setLoading(false);
      return;
    }
    setLoading(true);
    if (
      myBlockMonthContext.myBlockMonthData?.blockList?.BLOCK_TYPE_MNT ===
      blockType.NONBIDDER
    )
      setIsNonBidder(true);

    /* filter out any pairing activities that do not have a corresponding pairingDetails array element */
    const blockMonthActivities =
      myBlockMonthContext.myBlockMonthData.activities.filter(
        (activity) =>
          !(
            activity.activityType === eventActivityType.PAIRING &&
            !activity?.pairingDetails
          )
      );

    if (blockMonthActivities?.length) {
      /* sort activites if there is more than one activity */
      if (blockMonthActivities.length > 1)
        sortBlockMonthActivities(blockMonthActivities);

      /* add reserve events if blockType is reserve and calendar is planned */
      if (
        myBlockMonthContext.myBlockMonthData.blockList?.BLOCK_TYPE_MNT ===
          blockType.RESERVE &&
        !actual
      ) {
        addReserveToPlanned(
          blockMonthActivities,
          myBlockMonthContext.myBlockMonthData
        );
        extendReserveToGDO6(blockMonthActivities);
      }

      /* filter out holiday events */
      let filteredActivities = blockMonthActivities.filter(
        (activity) => activity.activityType !== eventActivityType.HOLIDAY
      );

      const holidayEvents = blockMonthActivities.filter(
        (activity) => activity.activityType === eventActivityType.HOLIDAY
      );

      /* if a TDO+6 event exists */
      if (
        blockMonthActivities.findIndex(
          (activity) => activity.activityType === eventActivityType.TDO6
        ) !== -1
      ) {
        filteredActivities = combineDO6(
          blockMonthActivities,
          eventActivityType.TDO6,
          eventActivityType.TDO
        );
      }
      /* if a GDO+6 event exists */
      if (
        blockMonthActivities.findIndex(
          (activity) => activity.activityType === eventActivityType.GDO6
        ) !== -1
      ) {
        filteredActivities = combineDO6(
          blockMonthActivities,
          eventActivityType.GDO6,
          eventActivityType.GDO
        );
      }
      /* if a release event exists */
      if (
        blockMonthActivities.findIndex(
          (activity) => activity.activityType === eventActivityType.RELEASE
        ) !== -1
      ) {
        filteredActivities = combineRAPsWithMiddleRelease(filteredActivities);
        mapReleaseToParentActivity(filteredActivities);

        /* filter out release events */
        filteredActivities = filteredActivities.filter(
          (event) => event.activityType !== eventActivityType.RELEASE
        );
      }
      assignSpecialEventStripCalendarHeightLevel(
        filteredActivities.filter((event) =>
          SPECIAL_EVENT_TYPES.includes(event.activityType)
        )
      );
      filteredActivities = removeEventsNotInBlockMonth(
        filteredActivities,
        myBlockMonthContext.myBlockMonthData.blockDates
      );
      setEventList({
        events: filteredActivities,
        holidayEvents: holidayEvents
      });
    } else {
      setEventList({
        events: [],
        holidayEvents: []
      });
    }
    setReservePilot(
      myBlockMonthContext.myBlockMonthData.blockList?.BLOCK_TYPE_MNT ===
        blockType.RESERVE
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBlockMonthContext.myBlockMonthData]);

  /* Set the selectedEvent on page load and when the block month changes */
  useEffect(() => {
    if (eventList.events.length === 0) {
      setLoading(false);
      return;
    }
    let dateDiffs: Array<number> = [];

    /* get the difference in days between today and event date */
    eventList.events.forEach((event) =>
      dateDiffs.push(
        Math.floor((Date.now() - Date.parse(event.start_date)) / 86400000)
      )
    );

    /* if an eventDate for today exists */
    if (dateDiffs.indexOf(0) !== -1)
      setSelectedEventIndex(dateDiffs.indexOf(0));
    else {
      const eventSpanningTodayIndex = eventList.events.findIndex(
        (ev) =>
          new Date(
            `${ev.start_date} ${formatNumToTime(ev.checkIn.value)}`
          ).getTime() <= new Date(new Date().toDateString()).getTime() &&
          new Date(new Date().toDateString()).getTime() <=
            new Date(
              `${ev.end_date} ${formatNumToTime(ev.checkOut.value)}`
            ).getTime()
      );
      if (eventSpanningTodayIndex !== -1)
        setSelectedEventIndex(eventSpanningTodayIndex);
      else {
        const futureDates = dateDiffs.filter((date) => date < 0);
        const pastDates = dateDiffs.filter((date) => date > 0);

        /* if an event exists after today's date, find the nearest event compared to today */
        if (futureDates.length > 0)
          setSelectedEventIndex(
            dateDiffs.indexOf(Math.max.apply(null, futureDates))
          );
        /* if an event exists before today's date, find the nearest event compared to today */ else if (
          pastDates.length > 0
        )
          setSelectedEventIndex(
            dateDiffs.indexOf(Math.min.apply(null, pastDates))
          );
      }
    }
    setLoading(false);
  }, [eventList]);

  const handleOnChangeActualToPlanned = () => {
    setPlannedToActual((prevState) => !prevState);
  };

  const handleOnCheck = () => {
    setChecked((prev) => !prev);
  };

  const handleOnClose = () => {
    setOpen(false);
    localStorage.setItem('showIADP', 'false');
  };

  const handleOnChangeToStripOrCalendarView = () => {
    setChangeToStripView((prevState) => !prevState);
  };

  const handleOnTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: number
  ) => {
    setTabValue(newValue);
  };

  const handleOnViewAllClick = () => {
    setViewAll((prevState) => !prevState);
  };

  const popup = () => {
    return {
      icon: 'icon-circle-flight',
      title: t('myBlockMonth.IADPPopupTitle'),
      description: t('myBlockMonth.IADPPopupDescription'),
      secondaryActionLabel: t('myBlockMonth.IADPPopupButton'),
      handleSecondaryAction: handleOnClose
    };
  };

  const handleOnPrintCalendar = () => {
    setPrintDialog(true);
  };

  const handlePrinting = useReactToPrint({
    content: () => stdCalPrintRef.current.contentDocument.body,
    copyStyles: true,
    pageStyle: () => {
      return `
                  @page {
                  size: a3 ${printView.current};
                  margin: 0;
                  }
                  @media print {
                      body {
                          -webkit-print-color-adjust: exact;
                          color-adjust: exact;
                      }
                  }
          `;
    },
    onBeforeGetContent: () => {
      setPrinting(true);
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (stdCalPrintRef.current) {
            clearInterval(interval);
            resolve(10);
          }
        }, 1000);
      });
    },
    onAfterPrint: () => {
      setPrinting(false);
    }
  });

  const startPrinting = (view: string) => {
    printView.current = view;
    setPrintDialog(false);
    handlePrinting();
  };

  const extractCSS = () => {
    const stylesheets = document.styleSheets;
    let cssRules = [] as any;
    for (let i = 0; i < stylesheets.length; i++) {
      try {
        const rules = stylesheets[i].cssRules || stylesheets[i].rules;
        for (let j = 0; j < rules.length; j++) {
          cssRules.push(rules[j].cssText);
        }
      } catch (e) {
        console.error('Cannot read styles from a cross-origin stylesheet');
      }
    }
    return cssRules.join('\n');
  };

  return profileContext.loading || myBlockMonthContext.loading || loading ? (
    <Loader />
  ) : (
    <>
      <MyBlockMonthHeader
        datesDropDownIndex={datesDropDownIndex}
        searchDate={searchDate}
        setSearchDate={setSearchDate}
        setDatesDropDownIndex={setDatesDropDownIndex}
        noData={!checkDataFromBlockMonthApiResponse(myBlockMonthContext)}
      />
      {!checkDataFromBlockMonthApiResponse(myBlockMonthContext) &&
        !isNonBidder && (
          <div className="c-mbm">
            <MyBlockMonthNoData searchDate={searchDate} />
          </div>
        )}
      <MyBlockMonthOverview
        datesDropDownIndex={datesDropDownIndex}
        isNonBidder={isNonBidder}
        noData={!checkDataFromBlockMonthApiResponse(myBlockMonthContext)}
      />
      <AcBackToTopButton />
      {stripView ? (
        <>
          <CalendarHeader
            actual={!actual}
            checked={checked}
            searchDate={searchDate}
            stripView={stripView}
            onHandleChange={handleOnChangeActualToPlanned}
            onHandleChangeOfCalendar={handleOnChangeToStripOrCalendarView}
            onHandleCheckOfHoliday={handleOnCheck}
            onHandlePrintCalendar={handleOnPrintCalendar}
            noData={!checkDataFromBlockMonthApiResponse(myBlockMonthContext)}
          />
          <StripCalendar
            actualView={actual}
            eventList={eventList.events}
            holidayEventList={eventList.holidayEvents}
            selectedEventIndex={selectedEventIndex}
            setSelectedEventIndex={setSelectedEventIndex}
            viewAll={viewAll}
            noData={!checkDataFromBlockMonthApiResponse(myBlockMonthContext)}
          />
          <StripCalendarLegend reservePilot={reservePilot} />
          <div className="c-mbm">
            {localStorage.getItem('showIADP') === 'true' && (
              <AcDialog open={open} onClose={handleOnClose} data={popup()} />
            )}
            <EventDetailsView
              actualView={actual}
              eventList={
                viewAll
                  ? eventList.events
                  : [eventList.events[selectedEventIndex]]
              }
              handleTabChange={handleOnTabChange}
              handleViewAllClick={handleOnViewAllClick}
              numberOfEvents={eventList.events.length}
              setSelectedEventIndex={setSelectedEventIndex}
              selectedEventIndex={selectedEventIndex}
              tabValue={tabValue}
              viewAll={viewAll}
              noData={
                !checkDataFromBlockMonthApiResponse(myBlockMonthContext) ||
                !eventList.events.length
              }
            />
          </div>
        </>
      ) : (
        <div>
          <MyBlockMonthPrintDialog
            open={printDialog}
            setOpen={setPrintDialog}
            startPrinting={startPrinting}
          />
          <CalendarHeader
            actual={!actual}
            checked={checked}
            searchDate={searchDate}
            stripView={stripView}
            onHandleChange={handleOnChangeActualToPlanned}
            onHandleChangeOfCalendar={handleOnChangeToStripOrCalendarView}
            onHandleCheckOfHoliday={handleOnCheck}
            onHandlePrintCalendar={handleOnPrintCalendar}
            noData={!checkDataFromBlockMonthApiResponse(myBlockMonthContext)}
          />
          <StandardCalendar
            actualView={actual}
            checked={checked}
            eventList={eventList.events}
            holidayEventList={eventList.holidayEvents}
            reservePilot={reservePilot}
            noData={!checkDataFromBlockMonthApiResponse(myBlockMonthContext)}
            document={document}
            window={window}
          />
          {printing && (
            <div
              className="c-mbm__print-view"
              style={{
                width: printView.current === 'landscape' ? '1500px' : '1200px',
                visibility: 'hidden',
                position: 'absolute',
                top: -9999,
                left: -9999,
                zIndex: -9999
              }}
            >
              <Frame
                ref={stdCalPrintRef}
                style={{
                  position: 'absolute',
                  top: -9999,
                  left: -9999,
                  visibility: 'hidden'
                }}
                head={
                  <>
                    <style>{extractCSS()}</style>
                  </>
                }
              >
                <FrameContextConsumer>
                  {({ document, window }) => (
                    <>
                      <CalendarHeader
                        actual={!actual}
                        checked={checked}
                        searchDate={searchDate}
                        stripView={stripView}
                        onHandleChange={handleOnChangeActualToPlanned}
                        onHandleChangeOfCalendar={
                          handleOnChangeToStripOrCalendarView
                        }
                        onHandleCheckOfHoliday={handleOnCheck}
                        onHandlePrintCalendar={handleOnPrintCalendar}
                        noData={
                          !checkDataFromBlockMonthApiResponse(
                            myBlockMonthContext
                          )
                        }
                      />
                      <StandardCalendar
                        actualView={actual}
                        checked={checked}
                        eventList={eventList.events}
                        holidayEventList={eventList.holidayEvents}
                        reservePilot={reservePilot}
                        noData={
                          !checkDataFromBlockMonthApiResponse(
                            myBlockMonthContext
                          )
                        }
                        document={document}
                        window={window}
                      />
                    </>
                  )}
                </FrameContextConsumer>
              </Frame>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MyBlockMonth;
